/**
 * @file ExpiryFields.tsx
 * @description «Произведено» + «Срок годности» строки приёмки: срок «мягко»
 * обязателен (phase19.22, W20; ui-design §7).
 *
 * Поведение:
 *   - есть дни (карточка товара / вид) — срок считается сам от даты
 *     изготовления: «✓ Рассчитано: 15.03.2026 + 365 дн. (≈ 12 мес.)» [изменить];
 *   - кладовщик вписал срок руками и он расходится с расчётом — подсказка
 *     «По карточке выходит …», не блок;
 *   - срока нет — янтарная рамка и «Срок не указан — партия будет помечена,
 *     на этикетке „—“»; добавить строку можно (W20 «мягко»);
 *   - вид «срок не требуется» — поля свёрнуты с подписью.
 * Контракт — общий `value/onChange` (как `CarrierMainFields`).
 *
 * phase19.31 (замечание владельца «поля Сроки не вписываются»): поля — общее
 * поле даты кабинета `DateField` (маска «дд.мм.гггг», календарь), подписи
 * обычным регистром. Логика пересчёта срока не менялась: значения те же
 * «гггг-мм-дд». Повторная приёмка: оба поля в одну линию на всех ширинах
 * (на 360 — две равные колонки), подписи — как у остальных полей формы.
 */

import { CheckCircle2, TriangleAlert } from "lucide-react";
import { useId, useRef } from "react";

import { DateField } from "../ui/DateField";
import {
  calculatedHint,
  computeExpiry,
  mismatchHint,
  withAutoExpiry,
  type ExpiryDates,
  type ExpiryPolicy,
} from "../../lib/receipt-expiry";

/** Даты строки приёмки (`lib/receipt-expiry.ts`). */
export type ExpiryValue = ExpiryDates;

interface Props {
  value: ExpiryValue;
  onChange: (next: ExpiryValue) => void;
  policy: ExpiryPolicy;
  /** Поля неактивны (панель приёмки без выбранной фасовки, Б15). */
  disabled?: boolean;
}

export function ExpiryFields({ value, onChange, policy, disabled = false }: Props) {
  const expiresRef = useRef<HTMLInputElement>(null);
  const id = useId();
  const calculated = computeExpiry(value.manufacturedAt, policy.shelfLifeDays);

  if (policy.requiresExpiry === false) {
    return (
      <p className="rounded-lg bg-surface-low px-3 py-2 text-sm text-on-surface-variant">
        Срок годности не ведётся{policy.kindName ? ` (вид: ${policy.kindName})` : ""}
      </p>
    );
  }

  function changeManufactured(manufacturedAt: string) {
    // Автосчёт, пока срок не вписан руками; стёрли дату — стёрся и автосрок.
    onChange(withAutoExpiry({ ...value, manufacturedAt }, policy.shelfLifeDays));
  }

  const empty = !value.expiresAt;
  const auto = !value.expiresManual && !!calculated && value.expiresAt === calculated;
  const mismatch = value.expiresManual ? mismatchHint(value.expiresAt, calculated) : null;

  return (
    <div className="flex flex-col gap-1.5">
      {/* Два поля в одну линию (замечание пользователя): каждое — под дату,
          не шире 11rem. До 640 — две колонки по 50%, но не уже 168px, иначе
          столбиком: в окне «Изменить позицию» на 360 поле показывало
          «08.10.202» (QA-5 D6; при 150px дата всё ещё не помещалась рядом с
          кнопкой календаря 48px — замер 84px под текст). Крестика нет: в паре он отнимал у даты
          половину поля, стирают клавиатурой. Подписи — как у остальных
          полей формы (SC43). */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(168px,1fr))] gap-2 sm:grid-cols-[repeat(2,minmax(0,11rem))] sm:gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <label htmlFor={`${id}-made`} className="label-editorial">
            Произведено
          </label>
          <DateField
            id={`${id}-made`}
            value={value.manufacturedAt}
            onChange={changeManufactured}
            title="Дата изготовления"
            disabled={disabled}
            clearable={false}
            className="sm:w-full"
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <label htmlFor={`${id}-exp`} className="label-editorial">
            Годен до
          </label>
          <DateField
            id={`${id}-exp`}
            inputRef={expiresRef}
            value={value.expiresAt}
            onChange={(expiresAt) => onChange({ ...value, expiresAt, expiresManual: expiresAt !== "" })}
            invalid={empty && !disabled}
            title="Срок годности"
            disabled={disabled}
            clearable={false}
            className="sm:w-full"
          />
        </div>
      </div>
      {disabled ? null : auto && calculated && policy.shelfLifeDays ? (
        <p className="type-caption flex flex-wrap items-center gap-x-2 text-on-surface-variant">
          <CheckCircle2 className="size-3.5 text-primary" aria-hidden />
          {calculatedHint(value.manufacturedAt, policy.shelfLifeDays, policy.source)}
          <button
            type="button"
            className="min-h-[var(--control-compact-h,3rem)] px-2 text-primary underline-offset-4 hover:underline"
            onClick={() => {
              onChange({ ...value, expiresManual: true });
              expiresRef.current?.focus();
            }}
          >
            изменить
          </button>
        </p>
      ) : null}
      {mismatch && !disabled ? <p className="type-caption text-[var(--status-attention-fg)]">{mismatch}</p> : null}
      {empty && !disabled ? (
        <p className="type-caption flex items-start gap-1.5 text-[var(--status-attention-fg)]">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          Срок не указан — партия будет помечена, на этикетке «—»
        </p>
      ) : null}
    </div>
  );
}
